import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api, fmt, fmtMoney, fmtDate } from "@/lib/api";
import { qtyText } from "@/components/QtyCells";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { WarehouseIcon } from "@/components/WarehouseBadge";
import { useAuth } from "@/hooks/useAuth";

const Stat = ({ label, value, accent, testId }) => (
  <div className="stat-card rise" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`font-display mt-2 text-3xl font-semibold ${accent ? "text-[#059669]" : ""}`}>{value}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);
  if (!d) return <p className="text-muted-foreground">Chargement…</p>;
  const t = d.totals;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Bonjour, ${user.name.split(" ")[0]}`} subtitle={user.role === "admin" ? "Vue globale de tous les dépôts" : "Vue de vos dépôts affectés"} />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Ventes aujourd'hui" value={fmtMoney(t.sales_today)} accent testId="stat-sales-today" />
        <Stat label="Total ventes" value={fmtMoney(t.sales_total)} testId="stat-sales-total" />
        <Stat label="Stock total (m)" value={fmt(t.stock_qty)} testId="stat-stock" />
        <Stat label="Alertes stock bas" value={t.low_stock} accent={t.low_stock > 0} testId="stat-low-stock" />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="rounded-lg border bg-card p-5 lg:col-span-3">
          <h3 className="font-display text-lg font-semibold">Ventes des 14 derniers jours</h3>
          <div className="mt-4 h-64">
            {d.sales_by_day.length === 0 ? <p className="text-sm text-muted-foreground">Aucune vente enregistrée.</p> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.sales_by_day}>
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} />
                  <YAxis fontSize={11} width={60} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="total" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="rounded-lg border bg-card p-5 lg:col-span-2" data-testid="dashboard-top-week">
          <div className="flex items-start justify-between gap-2">
            <div><h3 className="font-display text-lg font-semibold">Top tissus de la semaine</h3><p className="text-xs text-muted-foreground">Du {d.week.from} au {d.week.to} · {d.week.count} vente(s) · {fmtMoney(d.week.total)}</p></div>
            <Link to="/rapports" className="text-xs font-semibold text-emerald-700 hover:underline" data-testid="dashboard-week-link">Rapport des ventes BALLA →</Link>
          </div>
          <ul className="mt-4 space-y-3">
            {d.week.top_fabrics.length === 0 && <li className="text-sm text-muted-foreground">Aucune vente cette semaine.</li>}
            {d.week.top_fabrics.map((f, i) => (
              <li key={f.name} className="flex items-center justify-between gap-2 text-sm" data-testid={`top-week-${i}`}><span className="truncate"><span className="mr-2 font-display text-muted-foreground">{i + 1}.</span>{f.name}</span><span className="shrink-0 text-right"><span className="font-semibold">{fmtMoney(f.total)}</span><span className="block text-xs text-muted-foreground">{qtyText(f)}</span></span></li>
            ))}
          </ul>
          {d.top_fabrics.length > 0 && <p className="mt-4 border-t pt-3 text-xs text-muted-foreground">Depuis le début : {d.top_fabrics.map((f) => `${f.name} (${fmt(f.quantity)})`).join(" · ")}</p>}
        </div>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border bg-card p-5">
          <h3 className="font-display text-lg font-semibold">Dépôts</h3>
          <div className="mt-4 space-y-3" data-testid="dashboard-warehouses">
            {d.warehouses.map((w) => (
              <div key={w.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/60 px-4 py-3">
                <div className="flex items-center gap-3"><WarehouseIcon warehouse={w} size={16} /><div><p className="font-semibold">{w.name}</p><Badge variant={w.type === "principal" ? "default" : "secondary"} className="mt-1">{w.type}</Badge></div></div>
                <div className="text-right text-sm"><p>{fmt(w.stock_qty)} m en stock</p><p className="text-muted-foreground">Ventes: {fmtMoney(w.sales_total)}</p>{w.low_stock > 0 && <p className="text-[#059669]">{w.low_stock} alerte(s)</p>}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border bg-card p-5">
          <h3 className="font-display text-lg font-semibold">Dernières ventes</h3>
          <ul className="mt-4 divide-y">
            {d.recent_sales.length === 0 && <li className="text-sm text-muted-foreground">Aucune vente.</li>}
            {d.recent_sales.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-2 text-sm"><div><p className="font-semibold">{s.number}</p><p className="text-xs text-muted-foreground">{s.warehouse_name} · {s.seller_name} · {fmtDate(s.created_at)}</p></div><span className="font-semibold">{fmtMoney(s.total)}</span></li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
